import React, { useState, useEffect } from "react"
import { useNavigate } from "react-router-dom"
import { apiClient } from "~/api/client"
import SlackMarkdown from "~/components/SlackMarkdown"
import AuthenticatedLayout from "~/components/AuthenticatedLayout"
import type { KnowledgeEntry } from "~/types"

const Inbox: React.FC = () => {
  const [entries, setEntries] = useState<KnowledgeEntry[]>([])
  const [loading, setLoading] = useState(true)
  const [statusFilter, setStatusFilter] = useState<string>("")
  const [searchQuery, setSearchQuery] = useState("")
  const [syncUrl, setSyncUrl] = useState("")
  const [syncing, setSyncing] = useState(false)
  const [syncError, setSyncError] = useState<string | null>(null)
  const [syncSuccess, setSyncSuccess] = useState(false)
  const navigate = useNavigate()

  useEffect(() => {
    loadEntries()
  }, [statusFilter, searchQuery])

  const loadEntries = async () => {
    setLoading(true)
    try {
      const data = await apiClient.getEntries({
        status: statusFilter || undefined,
        q: searchQuery || undefined,
      })
      setEntries(data)
    } catch {
      // Failed to load entries
    } finally {
      setLoading(false)
    }
  }

  const formatDate = (dateString: string) => {
    const date = new Date(dateString)
    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    })
  }

  const handleSyncFromUrl = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!syncUrl.trim()) {
      return
    }

    setSyncing(true)
    setSyncError(null)
    setSyncSuccess(false)

    try {
      await apiClient.syncFromUrl(syncUrl)
      setSyncSuccess(true)
      setSyncUrl("")
      // Reload entries to show newly synced thread
      await loadEntries()
      // Clear success message after 3 seconds
      setTimeout(() => setSyncSuccess(false), 3000)
    } catch (error) {
      setSyncError(
        error instanceof Error ? error.message : "Failed to sync thread",
      )
    } finally {
      setSyncing(false)
    }
  }

  const getStatusBadgeStyle = (status: string) => {
    if (status === "resolved") {
      return {
        backgroundColor: "#d4edda",
        color: "#155724",
        padding: "4px 8px",
        borderRadius: "8px",
        fontSize: "12px",
        fontWeight: 600,
      }
    }
    return {
      backgroundColor: "#fff3cd",
      color: "#856404",
      padding: "4px 8px",
      borderRadius: "8px",
      fontSize: "12px",
      fontWeight: 600,
    }
  }

  return (
    <AuthenticatedLayout>
      <div style={{ maxWidth: "1200px", margin: "0 auto", padding: "0 24px" }}>
        <div
          style={{
            display: "flex",
            gap: "16px",
            marginBottom: "24px",
            alignItems: "center",
          }}
        >
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            style={{
              padding: "8px 12px",
              borderRadius: "8px",
              border: "1px solid #ccc",
              fontSize: "14px",
            }}
          >
            <option value="">All Status</option>
            <option value="open">Open</option>
            <option value="resolved">Resolved</option>
          </select>

          <input
            type="text"
            placeholder="Search..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            style={{
              flex: 1,
              padding: "8px 12px",
              borderRadius: "8px",
              border: "1px solid #ccc",
              fontSize: "14px",
            }}
          />
        </div>

        <form onSubmit={handleSyncFromUrl} style={{ marginBottom: "24px" }}>
          <div
            style={{
              backgroundColor: "white",
              padding: "16px",
              borderRadius: "8px",
              border: "1px solid #ccc",
            }}
          >
            <label
              style={{
                display: "block",
                fontWeight: 600,
                marginBottom: "8px",
              }}
            >
              Add Thread From URL
            </label>
            <div style={{ display: "flex", gap: "8px" }}>
              <input
                type="text"
                placeholder="Paste Slack thread URL..."
                value={syncUrl}
                onChange={(e) => setSyncUrl(e.target.value)}
                disabled={syncing}
                style={{
                  flex: 1,
                  padding: "8px 12px",
                  borderRadius: "8px",
                  border: "1px solid #ccc",
                  fontSize: "14px",
                }}
              />
              <button
                type="submit"
                disabled={syncing || !syncUrl.trim()}
                style={{
                  padding: "8px 16px",
                  backgroundColor: syncing ? "#6c757d" : "#007bff",
                  color: "white",
                  border: "none",
                  borderRadius: "8px",
                  fontSize: "14px",
                  fontWeight: 600,
                  cursor: syncing ? "not-allowed" : "pointer",
                }}
              >
                {syncing ? "Syncing..." : "Sync"}
              </button>
            </div>
            {syncError && (
              <div
                style={{
                  marginTop: "8px",
                  padding: "8px",
                  backgroundColor: "#f8d7da",
                  color: "#721c24",
                  borderRadius: "8px",
                  fontSize: "14px",
                }}
              >
                {syncError}
              </div>
            )}
            {syncSuccess && (
              <div
                style={{
                  marginTop: "8px",
                  padding: "8px",
                  backgroundColor: "#d4edda",
                  color: "#155724",
                  borderRadius: "8px",
                  fontSize: "14px",
                }}
              >
                Thread synced successfully!
              </div>
            )}
          </div>
        </form>

        {loading ? (
          <div style={{ textAlign: "center", padding: "48px" }}>Loading...</div>
        ) : entries.length === 0 ? (
          <div style={{ textAlign: "center", padding: "48px", color: "#666" }}>
            No entries found
          </div>
        ) : (
          <div
            style={{
              backgroundColor: "white",
              borderRadius: "8px",
              border: "1px solid #ccc",
              overflow: "hidden",
            }}
          >
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr
                  style={{
                    backgroundColor: "#f8f9fa",
                    borderBottom: "1px solid #ccc",
                  }}
                >
                  <th
                    style={{
                      padding: "12px 16px",
                      textAlign: "left",
                      fontWeight: 600,
                    }}
                  >
                    Title
                  </th>
                  <th
                    style={{
                      padding: "12px 16px",
                      textAlign: "left",
                      fontWeight: 600,
                    }}
                  >
                    Channel
                  </th>
                  <th
                    style={{
                      padding: "12px 16px",
                      textAlign: "left",
                      fontWeight: 600,
                    }}
                  >
                    Date
                  </th>
                  <th
                    style={{
                      padding: "12px 16px",
                      textAlign: "left",
                      fontWeight: 600,
                    }}
                  >
                    Status
                  </th>
                  <th
                    style={{
                      padding: "12px 16px",
                      textAlign: "left",
                      fontWeight: 600,
                    }}
                  >
                    Messages
                  </th>
                </tr>
              </thead>
              <tbody>
                {entries.map((entry) => (
                  <tr
                    key={entry.id}
                    onClick={() => navigate(`/entries/${entry.id}`)}
                    style={{
                      cursor: "pointer",
                      borderBottom: "1px solid #eee",
                    }}
                    onMouseEnter={(e) =>
                      (e.currentTarget.style.backgroundColor = "#f8f9fa")
                    }
                    onMouseLeave={(e) =>
                      (e.currentTarget.style.backgroundColor = "white")
                    }
                  >
                    <td style={{ padding: "12px 16px" }}>
                      <SlackMarkdown inline>
                        {entry.title || "Untitled"}
                      </SlackMarkdown>
                    </td>
                    <td style={{ padding: "12px 16px" }}>
                      #{entry.channel_name}
                    </td>
                    <td style={{ padding: "12px 16px" }}>
                      {formatDate(entry.created_at)}
                    </td>
                    <td style={{ padding: "12px 16px" }}>
                      <span style={getStatusBadgeStyle(entry.status)}>
                        {entry.status}
                      </span>
                    </td>
                    <td style={{ padding: "12px 16px" }}>
                      {entry.message_count}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </AuthenticatedLayout>
  )
}

export default Inbox
